import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
// import 'leaflet/dist/leaflet.css';
import { 
  Search, 
  Filter, 
  Plus, 
  Activity, 
  Calendar, 
  Clock, 
  AlertTriangle, 
  LayoutGrid, 
  List,
  Home,
  BarChart2,
  FileText,
  Settings,
  Users,
  Bell,
  Layers,
  Building2,
  Target
} from 'lucide-react';
import ProjectsSystemInfo from './SystemInfo/ProjectsSystemInfo';

// Delete default Leaflet icon configuration
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const GlassCard = ({ children, className = '' }) => (
  <div className={`bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 ${className}`}>
    {children}
  </div>
);

const NavButton = ({ active, icon: Icon, children, onClick, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
      active 
        ? 'bg-indigo-600 text-white shadow-lg' 
        : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'
    }`}
  >
    <Icon className="w-5 h-5 mr-2" />
    <span>{children}</span>
    {count !== undefined && (
      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
        active 
          ? 'bg-white text-indigo-600' 
          : 'bg-gray-100 text-gray-600'
      }`}>
        {count}
      </span>
    )}
  </button>
);

const StatusBadge = ({ status, count }) => {
  const colors = {
    'On Track': 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    'At Risk': 'bg-amber-100 text-amber-700 border border-amber-200',
    'Delayed': 'bg-rose-100 text-rose-700 border border-rose-200',
    'Completed': 'bg-blue-100 text-blue-700 border border-blue-200'
  };

  return (
    <div className="flex items-center">
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[status]}`}>
        {status}
      </span>
      {count !== undefined && (
        <span className="ml-2 text-sm text-gray-500">({count})</span>
      )}
    </div>
  );
};

const PhaseBadge = ({ phase, count }) => {
  const colors = {
    'Site Selection': 'bg-purple-100 text-purple-700 border border-purple-200',
    'Design': 'bg-blue-100 text-blue-700 border border-blue-200',
    'Construction': 'bg-orange-100 text-orange-700 border border-orange-200',
    'Opening': 'bg-green-100 text-green-700 border border-green-200'
  };

  return (
    <div className="flex items-center">
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[phase]}`}>
        {phase}
      </span>
      {count !== undefined && (
        <span className="ml-2 text-sm text-gray-500">({count})</span>
      )}
    </div>
  );
};
const CHART_COLORS = {
  'On Track': '#10B981',
  'At Risk': '#F59E0B',
  'Delayed': '#EF4444',
  'Completed': '#3B82F6'
};

const ProjectsDashboard = () => {
  // States
  const [activeTab, setActiveTab] = useState('Overview');
  const [viewMode, setViewMode] = useState('grid');
  const [projectStats, setProjectStats] = useState({
    totalProjects: 0,
    onTrack: 0,
    atRisk: 0,
    delayed: 0,
    completed: 0,
    completedThisMonth: 0,
    ongoing: 0,
    totalStores: 85,
    activeStores: 62,
    storesTarget: 100
  });
  const [projects, setProjects] = useState([]);
  const [projectsByPhase, setProjectsByPhase] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [phaseFilter, setPhaseFilter] = useState('All');

  // Constants
  const phaseOrder = ['Site Selection', 'Design', 'Construction', 'Opening'];

  const statusColors = {
    'On Track': '#10B981',
    'At Risk': '#F59E0B',
    'Delayed': '#EF4444',
    'Completed': '#3B82F6'
  };

  const phaseColors = {
    'Site Selection': '#E879F9',
    'Design': '#60A5FA',
    'Construction': '#FB923C',
    'Opening': '#4ADE80'
  };

  const navItems = [
    { id: 'Overview', icon: Home, label: 'Overview' },
    { id: 'Project Tracking', icon: Layers, label: 'Project Tracking' },
    { id: 'Teams', icon: Users, label: 'Teams' },
    { id: 'Analytics', icon: BarChart2, label: 'Analytics' },
    { id: 'Reports', icon: FileText, label: 'Reports' },
    { id: 'Notifications', icon: Bell, label: 'Notifications', count: 5 },
    { id: 'Settings', icon: Settings, label: 'Settings' }
  ];

  // Helper Functions
  const getMarkerIcon = (status, phase) => {
    return L.divIcon({
      className: 'custom-icon',
      html: `
        <div style="
          width: 30px; 
          height: 30px; 
          border-radius: 50%; 
          background-color: ${phaseColors[phase]}; 
          border: 3px solid ${statusColors[status]};
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          color: #333;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        ">
          ${phase[0]}
        </div>
      `,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });
  };

  const filteredProjects = useMemo(() => {
    return projects.filter(project => 
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (statusFilter === 'All' || project.status === statusFilter) &&
      (phaseFilter === 'All' || project.phase === phaseFilter)
    );
  }, [projects, searchTerm, statusFilter, phaseFilter]);

  const customChartTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 rounded shadow border">
          <p className="text-sm font-medium">{payload[0].name}</p>
          <p className="text-sm text-gray-600">{`${payload[0].value} Projects`}</p>
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    fetchProjectData();
  }, []);

  const fetchProjectData = async () => {
    // Simulated API call
    const mockProjects = [
      { 
        id: 1, 
        name: 'Olaya Road', 
        status: 'On Track', 
        phase: 'Construction', 
        location: [24.728454, 46.655172], 
        progress: 65,
        budget: '2.5M',
        deadline: '2024-12-31'
      },
      { 
        id: 2, 
        name: 'Hafr Albatin', 
        status: 'At Risk', 
        phase: 'Design', 
        location: [28.407294, 45.967847], 
        progress: 30,
        budget: '1.8M',
        deadline: '2024-10-15'
      },
      { 
        id: 3, 
        name: 'Alahsa', 
        status: 'Delayed', 
        phase: 'Site Selection', 
        location: [25.354479, 49.600574], 
        progress: 15,
        budget: '3.2M',
        deadline: '2024-11-30'
      },
      { 
        id: 4, 
        name: 'Alzulfi', 
        status: 'Completed', 
        phase: 'Opening', 
        location: [25.898781, 45.387049], 
        progress: 100,
        budget: '2.1M',
        deadline: '2024-08-20'
      },
      { 
        id: 5, 
        name: 'New Store E', 
        status: 'On Track', 
        phase: 'Site Selection', 
        location: [26.300145, 44.815849], 
        progress: 20,
        budget: '2.8M',
        deadline: '2025-01-15'
      },
    ];

    setProjects(mockProjects);

    const stats = mockProjects.reduce((acc, project) => {
      acc.totalProjects++;
      acc[project.status.toLowerCase().replace(' ', '')] = (acc[project.status.toLowerCase().replace(' ', '')] || 0) + 1;
      if (project.status !== 'Completed') acc.ongoing++;
      return acc;
    }, { 
      totalProjects: 0, 
      ontrack: 0, 
      atrisk: 0, 
      delayed: 0, 
      completed: 0, 
      ongoing: 0, 
      completedThisMonth: 2,
      totalStores: 85,
      activeStores: 62,
      storesTarget: 100
    });

    setProjectStats(stats);

    const phaseCount = mockProjects.reduce((acc, project) => {
      acc[project.phase] = (acc[project.phase] || 0) + 1;
      return acc;
    }, {});

    const orderedPhases = phaseOrder.map(phase => ({
      name: phase,
      value: phaseCount[phase] || 0,
      count: phaseCount[phase] || 0
    }));

    setProjectsByPhase(orderedPhases);
  };
  const renderOverviewTab = () => (
    <>
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <GlassCard className="p-6 hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center">
            <Activity className="w-12 h-12 text-emerald-500 bg-emerald-100 p-2 rounded-xl" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Active Projects</p>
              <h3 className="text-2xl font-bold text-gray-800">{projectStats.ongoing}</h3>
            </div>
          </div>
        </GlassCard>
        
        <GlassCard className="p-6 hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center">
            <Calendar className="w-12 h-12 text-blue-500 bg-blue-100 p-2 rounded-xl" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Completed This Month</p>
              <h3 className="text-2xl font-bold text-gray-800">{projectStats.completedThisMonth}</h3>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6 hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center">
            <Clock className="w-12 h-12 text-amber-500 bg-amber-100 p-2 rounded-xl" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">In Progress</p>
              <h3 className="text-2xl font-bold text-gray-800">{projectStats.ongoing - projectStats.delayed}</h3>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6 hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-center">
            <AlertTriangle className="w-12 h-12 text-rose-500 bg-rose-100 p-2 rounded-xl" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Delayed</p>
              <h3 className="text-2xl font-bold text-gray-800">{projectStats.delayed}</h3>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Search and Filters */}
      <GlassCard className="p-4 mb-8">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="flex gap-4 items-center">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white/50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="All">All Statuses</option>
              <option value="On Track">On Track ({projectStats.ontrack})</option>
              <option value="At Risk">At Risk ({projectStats.atrisk})</option>
              <option value="Delayed">Delayed ({projectStats.delayed})</option>
              <option value="Completed">Completed ({projectStats.completed})</option>
            </select>
            <select
              value={phaseFilter}
              onChange={(e) => setPhaseFilter(e.target.value)}
              className="bg-white/50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="All">All Phases</option>
              {projectsByPhase.map(phase => (
                <option key={phase.name} value={phase.name}>
                  {phase.name} ({phase.count})
                </option>
              ))}
            </select>
          </div>
        </div>
      </GlassCard>

      {/* Map Section */}
      <GlassCard className="p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Project Locations</h2>
          <div className="flex gap-2">
            {Object.entries(phaseColors).map(([phase, color]) => (
              <div key={phase} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-sm text-gray-600">{phase}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="h-[420px] rounded-xl overflow-hidden">
          <MapContainer 
            center={[24.706664, 46.709795]} 
            zoom={6} 
            className="h-full w-full"
            zoomControl={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {projects.map((project) => (
              <Marker 
                key={project.id} 
                position={project.location}
                icon={getMarkerIcon(project.status, project.phase)}
              >
                <Popup>
                  <div className="text-center p-3">
                    <h3 className="font-bold text-lg mb-2">{project.name}</h3>
                    <p className="mb-1">
                      Status: 
                      <span 
                        className="ml-1 px-2 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: statusColors[project.status], color: 'white' }}
                      >
                        {project.status}
                      </span>
                    </p>
                    <p className="mb-3">
                      Phase: 
                      <span 
                        className="ml-1 px-2 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: phaseColors[project.phase], color: '#333' }}
                      >
                        {project.phase}
                      </span>
                    </p>
                    <Link 
                      to={`/projects/${project.id}`}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View Project Details
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </GlassCard>
      {/* Charts and Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        {/* Pie Chart */}
        <GlassCard className="p-6 col-span-1">
          <h2 className="text-xl font-semibold mb-4">Status Distribution</h2>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'On Track', value: projectStats.ontrack },
                    { name: 'At Risk', value: projectStats.atrisk },
                    { name: 'Delayed', value: projectStats.delayed },
                    { name: 'Completed', value: projectStats.completed }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {Object.values(CHART_COLORS).map((color, index) => (
                    <Cell key={`cell-${index}`} fill={color} />
                  ))}
                </Pie>
                <Tooltip content={customChartTooltip} />
                <Legend verticalAlign="bottom" height={36} iconSize={8} fontSize={12} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        {/* Bar Chart */}
        <GlassCard className="p-6 col-span-1">
          <h2 className="text-xl font-semibold mb-4">Projects by Phase</h2>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={projectsByPhase} 
                layout="horizontal"
                margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
              >
                <XAxis dataKey="name" fontSize={11} angle={-45} textAnchor="end" height={60} />
                <YAxis fontSize={12} />
                <Tooltip content={customChartTooltip} />
                <Bar dataKey="value" fill="#818CF8" radius={[4, 4, 0, 0]}>
                  {projectsByPhase.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={Object.values(phaseColors)[index]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        {/* Summary Stats */}
        <GlassCard className="p-6">
          <h2 className="text-xl font-semibold mb-4">Summary Statistics</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-xl">
              <div className="flex items-center">
                <Building2 className="w-5 h-5 text-indigo-600 mr-3" />
                <span className="text-gray-600 text-sm">Total Stores</span>
              </div>
              <span className="font-bold text-lg">{projectStats.totalStores}</span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl">
              <div className="flex items-center">
                <Activity className="w-5 h-5 text-emerald-600 mr-3" />
                <span className="text-gray-600 text-sm">Active Stores</span>
              </div>
              <span className="font-bold text-lg">{projectStats.activeStores}</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
              <div className="flex items-center">
                <Layers className="w-5 h-5 text-blue-600 mr-3" />
                <span className="text-gray-600 text-sm">Total Projects</span>
              </div>
              <span className="font-bold text-lg">{projectStats.totalProjects}</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
              <div className="flex items-center">
                <Target className="w-5 h-5 text-purple-600 mr-3" />
                <span className="text-gray-600 text-sm">Stores Target</span>
              </div>
              <span className="font-bold text-lg">{projectStats.storesTarget}</span>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Projects List/Grid View */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Projects ({filteredProjects.length})</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' 
                ? 'bg-indigo-100 text-indigo-600' 
                : 'text-gray-500 hover:bg-gray-100'}`}
            >
              <LayoutGrid size={20} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' 
                ? 'bg-indigo-100 text-indigo-600' 
                : 'text-gray-500 hover:bg-gray-100'}`}
            >
              <List size={20} />
            </button>
          </div>
        </div>

        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <GlassCard key={project.id} className="p-6 hover:shadow-xl transition-all duration-200">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-semibold text-lg">{project.name}</h3>
                  <StatusBadge status={project.status} />
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Phase</p>
                    <PhaseBadge phase={project.phase} />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Progress</span>
                      <span className="font-medium">{project.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Budget</span>
                      <span className="font-medium">{project.budget} SAR</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Deadline</span>
                      <span className="font-medium">{new Date(project.deadline).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="pt-4 flex justify-end gap-3">
                    <Link 
                      to={`/projects/${project.id}`}
                      className="px-3 py-1 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
                    >
                      View Details
                    </Link>
                    <Link 
                      to={`/projects/${project.id}/edit`}
                      className="px-3 py-1 border border-indigo-600 text-indigo-600 rounded-lg text-sm hover:bg-indigo-50 transition-colors"
                    >
                      Edit
                    </Link>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        ) : (
          <GlassCard className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Project Name</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Phase</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Progress</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Budget</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Deadline</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200/50">
                  {filteredProjects.map((project) => (
                    <tr key={project.id} className="hover:bg-gray-50/30 transition-colors duration-150">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{project.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={project.status} />
                      </td>
                      <td className="px-6 py-4">
                        <PhaseBadge phase={project.phase} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-indigo-600 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${project.progress}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">{project.progress}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {project.budget} SAR
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {new Date(project.deadline).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-3">
                          <Link 
                            to={`/projects/${project.id}`}
                            className="px-3 py-1 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
                          >
                            View
                          </Link>
                          <Link 
                            to={`/projects/${project.id}/edit`}
                            className="px-3 py-1 border border-indigo-600 text-indigo-600 rounded-lg text-sm hover:bg-indigo-50 transition-colors"
                          >
                            Edit
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-transparent p-6">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Projects Dashboard</h1>
            <p className="text-gray-500">Monitoring {projectStats.totalProjects} projects across Saudi Arabia</p>
          </div>
          <Link 
            to="/projects/new" 
            className="inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Project
          </Link>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b mb-8">
          <div className="flex justify-center gap-4 pb-4">
            {navItems.map((item) => (
              <NavButton 
                key={item.id}
                active={activeTab === item.id} 
                onClick={() => setActiveTab(item.id)}
                icon={item.icon}
                count={item.count}
              >
                {item.label}
              </NavButton>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {activeTab === 'Overview' ? renderOverviewTab() : (
        <div className="p-6 text-center text-gray-500">
          {activeTab} content coming soon...
        </div>
      )}

      {/* System Information Component */}
      <ProjectsSystemInfo />
    </div>
  );
};

export default ProjectsDashboard;